import React from "react";
import { cn } from "../lib/utils";
import { Badge } from "./ui/badge";
import {
  Brush,
  Eye,
  RotateCcw,
  Users,
  Check,
  X,
  Clock,
  AlertTriangle,
} from "lucide-react";

const PHASE_CONFIG = {
  artist_work: { label: "Artist Work", icon: Brush, color: "sky" },
  ad_review: { label: "AD Review", icon: Eye, color: "purple" },
  artist_revision: { label: "Revision", icon: RotateCcw, color: "orange" },
  client_review: { label: "Client Review", icon: Users, color: "emerald" },
};

const colorMap = {
  sky: { bg: "bg-sky-500/10", text: "text-sky-400", border: "border-sky-500/30", dot: "bg-sky-400" },
  purple: { bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/30", dot: "bg-purple-400" },
  orange: { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30", dot: "bg-orange-400" },
  emerald: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", dot: "bg-emerald-400" },
};

function timeRemaining(deadlineStr) {
  if (!deadlineStr) return null;
  const now = new Date();
  const deadline = new Date(deadlineStr);
  const diffMs = deadline - now;
  if (diffMs <= 0) {
    const overMs = Math.abs(diffMs);
    const overHrs = Math.floor(overMs / 3600000);
    const overDays = Math.floor(overHrs / 24);
    if (overDays > 0) return { text: `${overDays}d overdue`, overdue: true };
    return { text: `${overHrs}h overdue`, overdue: true };
  }
  const hrs = Math.floor(diffMs / 3600000);
  const days = Math.floor(hrs / 24);
  const remainHrs = hrs % 24;
  if (days > 0) return { text: `${days}d ${remainHrs}h left`, overdue: false };
  const mins = Math.floor((diffMs % 3600000) / 60000);
  if (hrs > 0) return { text: `${hrs}h ${mins}m left`, overdue: false };
  return { text: `${mins}m left`, overdue: false };
}

function formatDate(isoStr) {
  if (!isoStr) return "";
  return new Date(isoStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function DeadlinePhasesCurrent({ phases = [] }) {
  const active = phases.find((p) => !p.completed_at);
  if (!active) return null;

  const config = PHASE_CONFIG[active.phase_type] || PHASE_CONFIG.artist_work;
  const colors = colorMap[config.color];
  const Icon = config.icon;
  const remaining = timeRemaining(active.deadline);

  return (
    <div
      className={cn("flex items-center gap-2 px-3 py-2 rounded-lg border", colors.bg, colors.border)}
      data-testid="deadline-phase-current"
    >
      <Icon className={cn("h-4 w-4 flex-shrink-0", colors.text)} />
      <span className={cn("text-sm font-medium", colors.text)}>{config.label}</span>
      {remaining && (
        <Badge
          className={cn(
            "text-xs ml-auto",
            remaining.overdue
              ? "bg-red-500/20 text-red-400 border-red-500/30"
              : "bg-zinc-800 text-zinc-300 border-zinc-700"
          )}
        >
          {remaining.overdue && <AlertTriangle className="h-3 w-3 mr-1" />}
          {!remaining.overdue && <Clock className="h-3 w-3 mr-1" />}
          {remaining.text}
        </Badge>
      )}
    </div>
  );
}

export function DeadlinePhasesTimeline({ phases = [] }) {
  if (!phases.length) return null;

  return (
    <div className="space-y-1" data-testid="deadline-phases-timeline">
      {phases.map((phase, idx) => {
        const config = PHASE_CONFIG[phase.phase_type] || PHASE_CONFIG.artist_work;
        const colors = colorMap[config.color];
        const Icon = config.icon;
        const isActive = !phase.completed_at;
        const remaining = isActive ? timeRemaining(phase.deadline) : null;

        return (
          <div
            key={phase.id || idx}
            className={cn(
              "flex items-center gap-2.5 px-3 py-2 rounded-md text-xs transition-all",
              isActive ? cn(colors.bg, "border", colors.border) : "bg-zinc-900/30"
            )}
          >
            {/* Timeline dot */}
            <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
              <div
                className={cn(
                  "w-2 h-2 rounded-full",
                  isActive ? cn(colors.dot, "animate-pulse") : phase.is_on_time === true ? "bg-green-500" : phase.is_on_time === false ? "bg-red-500" : "bg-zinc-600"
                )}
              />
            </div>

            <Icon className={cn("h-3.5 w-3.5 flex-shrink-0", isActive ? colors.text : "text-zinc-600")} />

            <div className="flex-1 min-w-0">
              <span className={cn("font-medium", isActive ? colors.text : "text-zinc-500")}>
                {config.label}
                {phase.version_number ? ` v${phase.version_number}` : ""}
              </span>
            </div>

            {/* Status */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {isActive && remaining && (
                <span className={cn("flex items-center gap-1", remaining.overdue ? "text-red-400" : "text-zinc-400")}>
                  {remaining.overdue ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                  {remaining.text}
                </span>
              )}
              {!isActive && phase.is_on_time === true && (
                <span className="flex items-center gap-1 text-green-500">
                  <Check className="h-3 w-3" /> On time
                </span>
              )}
              {!isActive && phase.is_on_time === false && (
                <span className="flex items-center gap-1 text-red-400">
                  <X className="h-3 w-3" /> Late
                </span>
              )}
              {!isActive && phase.is_on_time === null && (
                <span className="text-zinc-600">
                  {formatDate(phase.completed_at)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function DeadlinePhasesBadge({ phases = [] }) {
  const active = phases.find((p) => !p.completed_at);
  if (!active) return null;

  const config = PHASE_CONFIG[active.phase_type] || PHASE_CONFIG.artist_work;
  const colors = colorMap[config.color];
  const remaining = timeRemaining(active.deadline);

  if (!remaining) {
    return (
      <Badge className={cn("text-[11px]", colors.bg, colors.text, colors.border)}>
        {config.label}
      </Badge>
    );
  }

  return (
    <Badge
      className={cn(
        "text-[11px]",
        remaining.overdue
          ? "bg-red-500/15 text-red-400 border-red-500/30"
          : cn(colors.bg, colors.text, colors.border)
      )}
    >
      {remaining.overdue && <AlertTriangle className="h-3 w-3 mr-0.5" />}
      {config.label}: {remaining.text}
    </Badge>
  );
}
